import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export const StrikeDialog = ({ event, onIssued }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/moderation/events/${event.id}/strike`, { note });
      toast.success(`Strike #${data.user_strikes} issued to ${event.user_email} — both of you were emailed`);
      setOpen(false);
      onIssued(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not issue strike");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`strike-btn-${event.id}`} title="Issue strike"
        className="w-8 h-8 flex items-center justify-center border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm transition-colors duration-200">
        <Gavel className="w-3.5 h-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-border rounded-sm" data-testid="strike-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Gavel className="w-4 h-4 text-[#FF3B30]" /> Issue a strike to {event.user_email}?
            </DialogTitle>
            <DialogDescription>
              Reserve strikes for messages that demonstrate malice — intended harm to others. The user will be
              emailed the strike, the flags, your note and their original message, with a reminder that it was
              forwarded to you under the transparency mandate. You'll get a copy too.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Note to the user (optional)</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="strike-note-input"
              placeholder="Why this message crossed the line."
              className="bg-background border-border rounded-sm text-sm focus-visible:ring-[#FF3B30]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm border-border" data-testid="strike-cancel-btn">Cancel</Button>
            <Button onClick={issue} disabled={busy} data-testid="confirm-strike-btn"
              className="rounded-sm bg-[#FF3B30] hover:bg-[#d62f26] text-white gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />}
              {busy ? "Issuing…" : "Issue strike"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
